import { message } from "antd"
import { withPointBalanceRefresh } from "~/common/billing/pointAction"
import { VideoGenerationRequestValidationError } from "~/common/video/videoGenerationRequest"
import {
  buildStoryboardPromptText,
  buildStoryboardPromptTextFromSource,
  parseStoryboardPayload,
  validateShotTimeline,
  validateStoryboardContent,
} from "~/common/video/storyboard"
import {
  getVideoModelSelectionError,
  getVideoReferenceAudioError,
} from "~/common/video/videoModels"
import { HotVideoAudioType } from "~/common/constants"
import {
  getRecreateGenerationError,
  getRecreateGenerationResources,
  getRecreateGenerationAudio,
  getRecreateImageUrls,
} from "~/common/video/recreate"
import { createHotVideo } from "~/api/task"
import { createStaffHotVideo } from "~/api/staff"
import { getHotVideoTaskId } from "~/common/tasks/hotVideoTask"
import { buildRecreateProductInfo } from "~/common/video/recreateProductInfo"
import { parseFrameCopyFrontendRemark } from "~/common/tasks/taskFrontendRemark"

import type { HotVideoState, HotVideoSetState, HotVideoGetState } from "./types"
import {
  requestGuard,
  promptSaver,
  getHotVideoGenerationOptions,
  canUseHotVideoOriginalAudio,
} from "./shared"

export const createVideoActions = (
  set: HotVideoSetState,
  get: HotVideoGetState,
): Pick<HotVideoState, "validateReplicaVideo" | "generateReplicaVideo"> => ({
  validateReplicaVideo: () => {
    const state = get()
    if (
      !canUseHotVideoOriginalAudio(state) &&
      state.backgroundMusicMode === "original"
    ) {
      void message.warning(
        "当前参考视频没有可用的原音轨，请选择 AI 自动生成背景音乐",
      )
      return false
    }
    const videoOptions = getHotVideoGenerationOptions(state)
    const {
      replicaPrompt: promptText,
      isRequesting,
      isRestoringTaskDraft,
    } = state
    const videoGenerationResources = getRecreateGenerationResources(state)
    if (isRequesting || isRestoringTaskDraft || state.isSubmittingVideoTask)
      return false
    if (
      state.staffPreview &&
      (!state.videoUrl.trim() || !videoGenerationResources.length)
    ) {
      void message.warning("请补齐参考视频和参考图片后再生成")
      return false
    }
    if (
      state.staffPreview &&
      (!Number.isInteger(videoOptions.duration) ||
        videoOptions.duration < 4 ||
        videoOptions.duration > 30)
    ) {
      void message.warning("Staff 视频生成时长必须为 4～30 秒的整数")
      return false
    }
    const referenceError = getRecreateGenerationError(state)
    if (referenceError) {
      void message.warning(referenceError)
      return false
    }
    const promptPayload = parseStoryboardPayload(promptText)

    if (promptPayload) {
      set({ showStoryboardValidation: true })
      const contentValidation = validateStoryboardContent(promptPayload.rows)
      if (contentValidation.errorMessage) {
        void message.error(contentValidation.errorMessage)
        return false
      }
      const timelineValidation = validateShotTimeline(
        promptPayload.rows.map((row) => ({
          endTime: row.end_time,
          id: row.shot_number,
          startTime: row.start_time,
        })),
        videoOptions.model,
      )

      if (timelineValidation.errorMessage) {
        void message.error(timelineValidation.errorMessage)
        return false
      }
    }

    const videoPrompt = promptPayload
      ? buildStoryboardPromptTextFromSource(promptText, promptPayload.rows) ||
        buildStoryboardPromptText(promptPayload)
      : promptText.trim()

    if (!videoPrompt) {
      void message.warning("请先生成复刻提示词")
      return false
    }

    const modelError = getVideoModelSelectionError(videoOptions.model, {
      resources: videoGenerationResources,
      duration: videoOptions.duration,
    })
    if (modelError) {
      void message.warning(modelError)
      return false
    }

    return true
  },

  generateReplicaVideo: async (onSubmitted) => {
    if (!get().validateReplicaVideo()) return false
    const state = get()
    const videoOptions = getHotVideoGenerationOptions(state)
    const {
      replicaPrompt: promptText,
      backgroundMusicMode,
      hasEditedScript,
    } = state
    const videoReferenceImageUrls = getRecreateImageUrls(state.videoImageUrls)
    const videoGenerationResources = getRecreateGenerationResources(state)
    const referenceAudio = getRecreateGenerationAudio(state)
    const promptPayload = parseStoryboardPayload(promptText)
    const videoPrompt = promptPayload
      ? buildStoryboardPromptTextFromSource(promptText, promptPayload.rows) ||
        buildStoryboardPromptText(promptPayload)
      : promptText.trim()

    const draftRevision = state.draftRevision
    const isStale = requestGuard.begin(
      () => get().draftRevision !== draftRevision,
    )
    set({ isRequesting: true, taskErrorMessage: "", status: "生成视频中..." })
    try {
      if (!(await get().saveReplicaPrompt()) || isStale()) return false
      const audioError = await getVideoReferenceAudioError(
        videoOptions.model,
        referenceAudio ? [referenceAudio.url] : [],
      )
      if (isStale()) return false
      if (audioError) {
        void message.warning(audioError)
        set({ status: "" })
        return false
      }
      if (get().resourcesPending) {
        set({ status: "" })
        return false
      }
      const createVideoParams: Omit<API.Task.CreateVideoTaskParams, "task_id"> =
        {
          timbre_url: referenceAudio?.url || "",
          audio_type:
            backgroundMusicMode === "original"
              ? HotVideoAudioType.original
              : HotVideoAudioType.ai,
          channel: videoOptions.model,
          duration: videoOptions.duration,
          image_urls: videoReferenceImageUrls,
          prompt: videoPrompt,
          resolution: videoOptions.resolution,
          ratio: videoOptions.ratio,
        }
      const response = await withPointBalanceRefresh(async () => {
        const preview = state.staffPreview
        if (preview) {
          const sourceChanged =
            state.videoUrl.trim() !== preview.parse_video.video_url
          return createStaffHotVideo({
            ...createVideoParams,
            task: {
              parse_video: {
                ...preview.parse_video,
                video_url: state.videoUrl.trim(),
                result_text: state.sourcePrompt,
                ...(sourceChanged
                  ? {
                      audio_url: "",
                      width: 0,
                      height: 0,
                      duration: Math.round(state.videoDuration || 0),
                    }
                  : {}),
              },
              optimize_prompt: {
                ...preview.optimize_prompt,
                product_info: {
                  ...preview.optimize_prompt.product_info,
                  ...buildRecreateProductInfo(state.productInfo),
                },
                image_urls: videoReferenceImageUrls,
                duration: videoOptions.duration,
                result_prompt: videoPrompt,
              },
              frontend_remark: JSON.stringify({
                ...parseFrameCopyFrontendRemark(preview.frontend_remark),
                channel: videoOptions.model,
              }),
            },
          })
        }
        const taskId = await get().ensureTaskId()
        if (isStale() || !taskId || get().resourcesPending) return undefined
        return createHotVideo(
          { ...createVideoParams, task_id: taskId },
          videoGenerationResources,
        )
      }, "user")
      if (isStale() || !response) return false
      const createVideo = response.data?.create_video

      if (!createVideo) {
        throw new Error("创建视频任务失败")
      }
      if (state.staffPreview) {
        const taskId = getHotVideoTaskId(response.data)
        if (!taskId) throw new Error("创建接口未返回任务 ID，请检查任务列表")
        set({ taskId })
      }
      promptSaver.reset()
      set({ canSaveScript: false })

      onSubmitted?.(
        hasEditedScript ? "用户修改过视频脚本" : "用户未修改视频脚本",
      )
      set({ isSubmittingVideoTask: true, status: "生成视频中..." })
      return true
    } catch (error) {
      if (isStale()) return false
      set({
        status: "生成视频失败",
        taskErrorMessage:
          error instanceof Error ? error.message : "生成视频失败，请重试",
      })

      if (error instanceof VideoGenerationRequestValidationError) {
        void message.warning(error.message)
      }

      return false
    } finally {
      if (!isStale()) set({ isRequesting: false })
    }
  },
})
